import type * as React from "react";
import { useCallback, useContext, useState } from "react";
import {
    OptInBanner,
    OptInBannerInner,
    OptInBannerInnerBlock,
    OptInBannerMainText,
    OptInBannerSecondaryText,
    StyledLink,
} from "./StyledComponents";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { NewLabel } from "pro-features/components/NewLabel/NewLabel";
import { Button, IconButton } from "@similarweb/ui-components";
import { i18nFilter } from "app/filters/i18nFilter";
import type { IWithUseBetaBranchPref } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/useBetaBranchPrefHook";
import { WithUseBetaBranchPref } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/useBetaBranchPrefHook";
import { BetaBranchContext } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/BetaBranchContext";
import { allTrackers } from "app/services/track/track";

const BetaBranchOptInBannerComponent: React.FC<IWithUseBetaBranchPref> = ({ useBetaBranchPref }) => {
    const [isVisible, setIsVisible] = useState(true);
    const { setIsBetaBranchFeedbackSurveyOpen } = useContext(BetaBranchContext);
    const onClose = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "Close notification banner");
        setIsVisible(false);
    }, []);
    const onOptIn = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "webanalysis.beta.branch.opt.in.banner.optIn",
            "click",
            "webanalysis.beta.branch.opt.in.banner.optIn",
        );
        useBetaBranchPref.changePref(true, "BetaOptInBanner");
    }, []);

    const onOptOut = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "webanalysis/BetaFeedbackBanner/OptOut");
        useBetaBranchPref.changePref(false, "BetaOptOutBanner");
    }, []);
    const onOpenFeedbackModal = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "webanalysis/BetaFeedbackBanner/FeedbackButton");
        setIsBetaBranchFeedbackSurveyOpen(true);
    }, []);
    return !isVisible ? (
        <div />
    ) : (
        <OptInBanner
            optedIn={useBetaBranchPref.value}
            data-automation="beta-branch-container"
        >
            <OptInBannerInner>
                <OptInBannerInnerBlock>
                    {!useBetaBranchPref.value && <NewLabel />}
                    <OptInBannerMainText>
                        <I18n>
                            {!useBetaBranchPref.value
                                ? "website.analysis.traffic.and.engagement.beta.data.banner.optin.main"
                                : "website.analysis.traffic.and.engagement.beta.data.banner.optout.main"}
                        </I18n>
                    </OptInBannerMainText>
                    <OptInBannerSecondaryText>
                        <I18n>
                            {!useBetaBranchPref.value
                                ? "website.analysis.traffic.and.engagement.beta.data.banner.optin.secondary"
                                : "website.analysis.traffic.and.engagement.beta.data.banner.optout.secondary"}
                        </I18n>
                    </OptInBannerSecondaryText>
                    <StyledLink
                        label={i18nFilter()(
                            "website.analysis.traffic.and.engagement.beta.data.banner.optin.learn.more",
                        )}
                        url={"https://support.similarweb.com/hc/en-us/articles/360018497238"}
                        target={"_blank"}
                    />
                </OptInBannerInnerBlock>
                <OptInBannerInnerBlock></OptInBannerInnerBlock>

                {!useBetaBranchPref.value ? (
                    <OptInBannerInnerBlock>
                        <Button onClick={onOptIn}>
                            {i18nFilter()("website.analysis.traffic.and.engagement.beta.data.banner.optin.button")}
                        </Button>
                        <OptInBannerInnerBlock>
                            <IconButton
                                iconSize={"sm"}
                                iconName="clear"
                                type="flat"
                                onClick={onClose}
                            />
                        </OptInBannerInnerBlock>
                    </OptInBannerInnerBlock>
                ) : (
                    <OptInBannerInnerBlock>
                        <IconButton
                            onClick={onOpenFeedbackModal}
                            type="primary"
                            iconName="response"
                        >
                            {i18nFilter()(
                                "website.analysis.traffic.and.engagement.beta.data.banner.open.feedback.button",
                            )}
                        </IconButton>
                        <OptInBannerInnerBlock>
                            <Button
                                onClick={onOptOut}
                                type="flat"
                            >
                                {i18nFilter()("website.analysis.traffic.and.engagement.beta.data.banner.optout.button")}
                            </Button>
                        </OptInBannerInnerBlock>
                    </OptInBannerInnerBlock>
                )}
            </OptInBannerInner>
        </OptInBanner>
    );
};

export default WithUseBetaBranchPref(BetaBranchOptInBannerComponent);
